import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/api';

const formatTimestamp = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const AdminPropertyDocumentsPanel = ({ addToast, onQueueChange }) => {
  const [pendingDocs, setPendingDocs] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [grantedAccess, setGrantedAccess] = useState([]);
  const [revokedAccess, setRevokedAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState('');
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, reqRes, grantsRes, revokedRes] = await Promise.all([
        apiClient.listPendingPropertyDocuments(),
        apiClient.listPropertyDocumentAccessRequests('pending'),
        apiClient.listGrantedPropertyDocumentAccess(),
        apiClient.listRevokedPropertyDocumentAccess(),
      ]);
      setPendingDocs(Array.isArray(docsRes.data) ? docsRes.data : []);
      setAccessRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
      setGrantedAccess(Array.isArray(grantsRes.data) ? grantsRes.data : []);
      setRevokedAccess(Array.isArray(revokedRes.data) ? revokedRes.data : []);
      onQueueChange?.();
    } catch (err) {
      addToastRef.current?.(err.message || 'Could not load document queue.', 'error');
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const reviewDoc = async (propertyId, docId, status) => {
    setReviewingId(docId);
    try {
      await apiClient.reviewPropertyDocument(propertyId, docId, status);
      addToast?.(status === 'approved' ? 'Document approved.' : 'Document rejected.', 'success');
      await load();
    } catch (err) {
      addToast?.(err.message || 'Review failed.', 'error');
    } finally {
      setReviewingId('');
    }
  };

  const reviewAccess = async (id, decision) => {
    setReviewingId(id);
    try {
      await apiClient.reviewPropertyDocumentAccess(id, decision);
      addToast?.(decision === 'approved' ? 'Access granted.' : 'Access denied.', 'success');
      await load();
    } catch (err) {
      addToast?.(err.message || 'Could not update access request.', 'error');
    } finally {
      setReviewingId('');
    }
  };

  const revokeAccess = async (grant) => {
    if (!window.confirm(`Revoke document download access for ${grant.userEmail || grant.userUid}?`)) return;
    setReviewingId(grant.id);
    try {
      await apiClient.revokePropertyDocumentAccess(grant.id);
      addToast?.('Access revoked.', 'success');
      await load();
    } catch (err) {
      addToast?.(err.message || 'Could not revoke access.', 'error');
    } finally {
      setReviewingId('');
    }
  };

  const regrantAccess = async (revocation) => {
    if (!window.confirm(`Re-grant document download access for ${revocation.userEmail || revocation.userUid}?`)) return;
    setReviewingId(revocation.id);
    try {
      await apiClient.regrantPropertyDocumentAccess(revocation.id);
      addToast?.('Access re-granted.', 'success');
      await load();
    } catch (err) {
      addToast?.(err.message || 'Could not re-grant access.', 'error');
    } finally {
      setReviewingId('');
    }
  };

  const hasActiveGrantForUser = (revocation) => grantedAccess.some(
    (grant) => String(grant.userUid) === String(revocation.userUid)
      && String(grant.propertyId) === String(revocation.propertyId),
  );

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-folder-open"></i> Property documents</h3>
          <p className="admin-panel-sub">Approve lister uploads, grant investor access, and revoke access when needed.</p>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
          <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`}></i> Refresh
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-light)' }}><i className="fas fa-spinner fa-spin"></i> Loading…</p>
      ) : (
        <>
          <h4 style={{ marginTop: '1.5rem' }}>Pending document uploads</h4>
          {pendingDocs.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>No uploads awaiting approval.</p>
          ) : (
            pendingDocs.map((doc) => (
              <div key={doc.id} className="agent-card">
                <div className="agent-header">
                  <div>
                    <h4>{doc.title}</h4>
                    <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>
                      {doc.propertyTitle ? `${doc.propertyTitle} · ` : ''}
                      Property {doc.propertyId} · {doc.uploadedByEmail || 'Unknown'} ({doc.uploadedByRole})
                    </p>
                  </div>
                  <span className="status-badge status-pending">Pending</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={reviewingId === doc.id}
                    onClick={() => reviewDoc(doc.propertyId, doc.id, 'approved')}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={reviewingId === doc.id}
                    onClick={() => reviewDoc(doc.propertyId, doc.id, 'rejected')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}

          <h4 style={{ marginTop: '2rem' }}>Pending download access requests</h4>
          {accessRequests.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>No pending access requests.</p>
          ) : (
            accessRequests.map((req) => (
              <div key={req.id} className="agent-card">
                <div className="agent-header">
                  <div>
                    <h4>{req.propertyTitle || req.propertyId}</h4>
                    <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>
                      {req.requesterEmail || req.requesterUid}
                    </p>
                    {req.message ? (
                      <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>{req.message}</p>
                    ) : null}
                  </div>
                  <span className="status-badge status-pending">Pending</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={reviewingId === req.id}
                    onClick={() => reviewAccess(req.id, 'approved')}
                  >
                    Grant access
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={reviewingId === req.id}
                    onClick={() => reviewAccess(req.id, 'denied')}
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))
          )}

          <h4 style={{ marginTop: '2rem' }}>Active access grants</h4>
          {grantedAccess.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>No investors currently have download access.</p>
          ) : (
            grantedAccess.map((grant) => (
              <div key={grant.id} className="agent-card">
                <div className="agent-header">
                  <div>
                    <h4>{grant.propertyTitle || grant.propertyId}</h4>
                    <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>
                      {grant.userEmail || grant.userUid}
                      {grant.grantedAt ? ` · granted ${new Date(grant.grantedAt).toLocaleDateString()}` : ''}
                    </p>
                    {grant.grantedByEmail || grant.grantedByName ? (
                      <p style={{ color: 'var(--text-light)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Granted by {grant.grantedByName || grant.grantedByEmail}
                      </p>
                    ) : null}
                  </div>
                  <span className="status-badge status-approved">Active</span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ color: 'var(--danger)', borderColor: 'rgba(220,53,69,0.35)' }}
                  disabled={reviewingId === grant.id}
                  onClick={() => revokeAccess(grant)}
                >
                  <i className={`fas fa-${reviewingId === grant.id ? 'spinner fa-spin' : 'user-slash'}`}></i> Revoke access
                </button>
              </div>
            ))
          )}

          <h4 style={{ marginTop: '2rem' }}>Revoked access history</h4>
          {revokedAccess.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>No revoked access records yet.</p>
          ) : (
            revokedAccess.map((row) => (
              <div key={row.id} className="agent-card">
                <div className="agent-header">
                  <div>
                    <h4>{row.propertyTitle || row.propertyId}</h4>
                    <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>
                      {row.userEmail || row.userUid}
                      {row.revokedAt ? ` · revoked ${formatTimestamp(row.revokedAt)}` : ''}
                    </p>
                    {row.revokedByEmail || row.revokedByName ? (
                      <p style={{ color: 'var(--text-light)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Revoked by {row.revokedByName || row.revokedByEmail}
                      </p>
                    ) : null}
                    {row.regrantedAt ? (
                      <p style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Re-granted {formatTimestamp(row.regrantedAt)}
                      </p>
                    ) : null}
                  </div>
                  <span className="status-badge status-rejected">Revoked</span>
                </div>
                {row.canRegrant && !row.regrantedAt && !hasActiveGrantForUser(row) ? (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={reviewingId === row.id}
                    onClick={() => regrantAccess(row)}
                  >
                    <i className={`fas fa-${reviewingId === row.id ? 'spinner fa-spin' : 'redo'}`}></i> Re-grant access
                  </button>
                ) : null}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default AdminPropertyDocumentsPanel;
